function PrivacyPage() {
  return (
    <div className="py-12 bg-slate-50">
      <div className="container mx-auto px-4 max-w-3xl">
        <h1 className="text-4xl font-bold mb-8 glow-text">Privacy Policy</h1>
        <div className="bg-white rounded-xl shadow-lg p-8">
          <p className="mb-4">Last Updated: July 16, 2026</p>
          
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Introduction</h2>
            <p className="mb-4">
              This Privacy Policy explains how Family Dashboard ("we", "our", or "us") collects, uses, and protects your information when you use our application.
            </p>
            <p>
              We take your privacy seriously and are committed to protecting your personal information. Please read this Privacy Policy carefully to understand our practices regarding your data.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Information We Collect</h2>
            <p className="mb-4">Family Dashboard may collect the following types of information:</p>
            
            <h3 className="text-xl font-semibold mt-6 mb-2">Google Account Information</h3>
            <p className="mb-4">
              With your explicit permission, we access:
            </p>
            <ul className="list-disc pl-5 mb-4 space-y-1">
              <li>Calendar events from Google Calendar (read-only)</li>
            </ul>
            <p>
              This access is granted through Google's OAuth authentication process, which you can revoke at any time. Family Dashboard requests only the read-only Google Calendar scope and does not access your Google profile, email address, or photos.
            </p>
            
            <h3 className="text-xl font-semibold mt-6 mb-2">Local Settings and Preferences</h3>
            <p className="mb-4">
              We store your application preferences locally on your device, including:
            </p>
            <ul className="list-disc pl-5 mb-4 space-y-1">
              <li>Weather location settings</li>
              <li>Display preferences</li>
              <li>Calendar display settings</li>
              <li>Authentication tokens (securely stored)</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">How We Use Your Information</h2>
            <p className="mb-4">We use your information solely to provide the Family Dashboard service, specifically to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Display your calendar events</li>
              <li>Show the photos you add from your phone or a local folder</li>
              <li>Provide weather information for your selected location</li>
              <li>Save your preferences and settings</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Data Storage and Security</h2>
            <p className="mb-4">Your data security is important to us:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Family Dashboard runs on your device. We operate no servers that receive your Google Calendar data.</li>
              <li>Photos come from your own phone or a local folder and stay on your device; they are never uploaded to us.</li>
              <li>Authentication tokens are stored on your device using industry-standard encryption</li>
              <li>We do not sell or share your data with third parties</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Third-party Services</h2>
            <p className="mb-4">Family Dashboard uses the following third-party services:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong>Google Calendar API</strong>: We access this service with your permission to display your calendar events.
              </li>
              <li>
                <strong>Open-Meteo API</strong>: We use this service to provide weather information based on the location you specify.
              </li>
              <li>
                <strong>Anthropic Claude API</strong>: Used to generate educational content for the Kids Activities feature.
              </li>
            </ul>
            <p className="mt-4">
              Each of these services has its own Privacy Policy, and we encourage you to review them.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Google API Services User Data Policy</h2>
            <p>
              Family Dashboard's use and transfer of information received from Google
              APIs to any other app will adhere to the{' '}
              <a
                href="https://developers.google.com/terms/api-services-user-data-policy"
                className="text-blue-600 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google API Services User Data Policy
              </a>
              , including the Limited Use requirements.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Your Rights and Choices</h2>
            <p className="mb-4">You have the following rights regarding your data:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Right to revoke access: You can revoke our access to your Google account at any time through your Google Account settings</li>
              <li>Right to delete: You can delete all local data by resetting the application or uninstalling it</li>
              <li>Right to modify: You can change your settings and preferences at any time through the application</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Children's Privacy</h2>
            <p className="mb-4">
              Family Dashboard is designed for family use, which may include children. The Kids Activities feature is designed for children's educational use. We do not knowingly collect personal information from children. All content displayed to children is controlled by the family account administrator.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Changes to This Privacy Policy</h2>
            <p className="mb-4">
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
            </p>
          </section>
          
          <section>
            <h2 className="text-2xl font-bold mb-4">Contact Us</h2>
            <p className="mb-4">
              If you have any questions about this Privacy Policy, please contact us at:
            </p>
            <div className="bg-gray-100 p-4 rounded">
              <p>Email: info@rykenlabs.com</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPage;